import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { generateIndustryServiceSchema } from '@/utils/seoSchema';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import HeroSection from '@/components/social-media/HeroSection';
import WhyKIVideos from '@/components/social-media/WhyKIVideos';
import ProcessSteps from '@/components/social-media/ProcessSteps';
import ExampleGallery from '@/components/social-media/ExampleGallery';
import FAQSection from '@/components/social-media/FAQSection';
import CtaSection from '@/components/social-media/CtaSection';
import FloatingCTA from '@/components/social-media/FloatingCTA';

const HealthcareIndustry = () => {
  const { lang, city } = useParams();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // SEO meta tags setup based on language
    const titlesByLang = {
      de: city ? `KI-Videomarketing für Gesundheitswesen ${city} | Future Level` : "KI-Videomarketing für Gesundheitswesen | Future Level",
      en: city ? `AI Video Marketing for Healthcare ${city} | Future Level` : "AI Video Marketing for Healthcare | Future Level",
      fr: city ? `Marketing Vidéo IA pour Santé ${city} | Future Level` : "Marketing Vidéo IA pour Santé | Future Level",
      it: city ? `Marketing Video AI per Sanità ${city} | Future Level` : "Marketing Video AI per Sanità | Future Level"
    };
    
    const descriptionsByLang = {
      de: city ? 
        `Vertrauenswürdige KI-Videos für Kliniken, Praxen und Gesundheitsdienstleister in ${city}. DSGVO-konform und ethisch verantwortungsvoll für den Gesundheitssektor.` : 
        "Vertrauenswürdige KI-Videos für Kliniken, Praxen und Gesundheitsdienstleister. DSGVO-konform und ethisch verantwortungsvoll für den Gesundheitssektor.",
      en: city ?
        `Trustworthy AI videos for clinics, practices, and healthcare providers in ${city}. GDPR compliant and ethically responsible for the healthcare sector.` :
        "Trustworthy AI videos for clinics, practices, and healthcare providers. GDPR compliant and ethically responsible for the healthcare sector.",
      fr: city ?
        `Vidéos IA fiables pour cliniques, cabinets et prestataires de santé à ${city}. Conformes au RGPD et éthiquement responsables pour le secteur de la santé.` :
        "Vidéos IA fiables pour cliniques, cabinets et prestataires de santé. Conformes au RGPD et éthiquement responsables pour le secteur de la santé.",
      it: city ?
        `Video AI affidabili per cliniche, studi medici e fornitori di servizi sanitari a ${city}. Conformi al GDPR e eticamente responsabili per il settore sanitario.` :
        "Video AI affidabili per cliniche, studi medici e fornitori di servizi sanitari. Conformi al GDPR e eticamente responsabili per il settore sanitario."
    };
    
    const keywordsByLang = {
      de: city ? 
        `Gesundheitswesen KI-Videos ${city}, Klinik Marketing Videos ${city}, Praxis Erklärvideo ${city}, Medizinische Marketingvideos, Patientenaufklärung Video` :
        "Gesundheitswesen KI-Videos, Klinik Marketing Videos, Praxis Erklärvideo, Medizinische Marketingvideos, Patientenaufklärung Video",
      en: city ?
        `Healthcare AI videos ${city}, clinic marketing videos ${city}, practice explainer video ${city}, medical marketing videos, patient education video` :
        "Healthcare AI videos, clinic marketing videos, practice explainer video, medical marketing videos, patient education video",
      fr: city ?
        `Vidéos IA santé ${city}, vidéos marketing clinique ${city}, vidéo explicative cabinet ${city}, vidéos marketing médical, vidéo éducation patient` :
        "Vidéos IA santé, vidéos marketing clinique, vidéo explicative cabinet, vidéos marketing médical, vidéo éducation patient",
      it: city ?
        `Video AI sanità ${city}, video marketing clinica ${city}, video esplicativo studio medico ${city}, video marketing medico, video educazione paziente` :
        "Video AI sanità, video marketing clinica, video esplicativo studio medico, video marketing medico, video educazione paziente"
    };
    
    // Determine the canonical URL path based on language
    const pagePath = currentLanguage === 'de' ? '/leistungen/ki-videomarketing-gesundheit' : 
                    currentLanguage === 'en' ? '/services/ai-video-marketing-healthcare' : 
                    currentLanguage === 'fr' ? '/services/video-marketing-ia-sante' : 
                    '/servizi/marketing-video-ia-sanita';
                    
    const canonicalUrl = getCanonicalUrl(`${pagePath}${city ? `/${city.toLowerCase()}` : ''}`, currentLanguage);
    
    // Update meta tags for SEO
    updateMetaTags({
      title: titlesByLang[currentLanguage as keyof typeof titlesByLang],
      description: descriptionsByLang[currentLanguage as keyof typeof descriptionsByLang],
      keywords: keywordsByLang[currentLanguage as keyof typeof keywordsByLang],
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      ogType: "website"
    });
    
    // Add structured data with industry-specific schema
    generateIndustryServiceSchema(
      'healthcare',
      titlesByLang[currentLanguage as keyof typeof titlesByLang],
      descriptionsByLang[currentLanguage as keyof typeof descriptionsByLang],
      "https://future-level.ch/opengraph-image-p98pqg.png",
      canonicalUrl,
      currentLanguage,
      city
    );
    
  }, [currentLanguage, city]);
  
  // Industry-specific content for healthcare
  const getHeroContent = () => {
    const content = {
      de: {
        title: city ? `KI-Videomarketing für Gesundheitswesen in ${city}` : 'KI-Videomarketing für Gesundheitswesen',
        subtitle: 'Vertrauenswürdige und ethisch verantwortungsvolle Video-Inhalte für Kliniken, Praxen und Gesundheitsdienstleister',
        description: 'Optimieren Sie Ihre Patientenkommunikation mit informativen und verständlichen KI-Videos. Erklären Sie medizinische Verfahren, Behandlungen und Dienstleistungen auf anschauliche Weise unter strikter Einhaltung des Datenschutzes.',
      },
      en: {
        title: city ? `AI Video Marketing for Healthcare in ${city}` : 'AI Video Marketing for Healthcare',
        subtitle: 'Trustworthy and ethically responsible video content for clinics, practices, and healthcare providers',
        description: 'Optimize your patient communication with informative and understandable AI videos. Explain medical procedures, treatments, and services in an illustrative way while strictly adhering to data privacy regulations.',
      },
      fr: {
        title: city ? `Marketing Vidéo IA pour Santé à ${city}` : 'Marketing Vidéo IA pour Santé',
        subtitle: 'Contenu vidéo fiable et éthiquement responsable pour cliniques, cabinets et prestataires de santé',
        description: 'Optimisez votre communication avec les patients grâce à des vidéos IA informatives et compréhensibles. Expliquez les procédures médicales, les traitements et les services de manière illustrative tout en respectant strictement les réglementations de confidentialité des données.',
      },
      it: {
        title: city ? `Marketing Video AI per Sanità a ${city}` : 'Marketing Video AI per Sanità',
        subtitle: 'Contenuti video affidabili ed eticamente responsabili per cliniche, studi medici e fornitori di servizi sanitari',
        description: 'Ottimizza la comunicazione con i pazienti con video AI informativi e comprensibili. Spiega procedure mediche, trattamenti e servizi in modo illustrativo rispettando rigorosamente le normative sulla privacy dei dati.',
      }
    };
    
    return content[currentLanguage as keyof typeof content] || content.en;
  };
  
  const heroContent = getHeroContent();
  
  return (
    <div className="flex flex-col min-h-screen bg-[#121212]">
      <Header />
      <PageTransition>
        <main className="flex-grow pt-24">
          <HeroSection 
            title={heroContent.title} 
            subtitle={heroContent.subtitle} 
            description={heroContent.description}
            industry="healthcare"
          />
          
          <WhyKIVideos industry="healthcare" />
          
          <ProcessSteps industry="healthcare" />
          
          <section className="py-16 px-4 md:px-8 bg-gradient-to-br from-purple-900/20 to-black">
            <div className="container mx-auto max-w-6xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white text-center">
                {currentLanguage === 'de' ? 'Gesundheitsspezifische Videolösungen' :
                 currentLanguage === 'en' ? 'Healthcare-Specific Video Solutions' :
                 currentLanguage === 'fr' ? 'Solutions Vidéo Spécifiques à la Santé' :
                 'Soluzioni Video Specifiche per la Sanità'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Patientenaufklärung' :
                     currentLanguage === 'en' ? 'Patient Education' :
                     currentLanguage === 'fr' ? 'Éducation des Patients' :
                     'Educazione dei Pazienti'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Erklären Sie medizinische Verfahren und Behandlungen mit leicht verständlichen Videos für Ihre Patienten.' :
                     currentLanguage === 'en' ? 'Explain medical procedures and treatments with easy-to-understand videos for your patients.' :
                     currentLanguage === 'fr' ? 'Expliquez les procédures médicales et les traitements avec des vidéos faciles à comprendre pour vos patients.' :
                     'Spiega procedure mediche e trattamenti con video facili da capire per i tuoi pazienti.'}
                  </p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Klinik & Praxispräsentation' :
                     currentLanguage === 'en' ? 'Clinic & Practice Presentation' :
                     currentLanguage === 'fr' ? 'Présentation de Clinique & Cabinet' :
                     'Presentazione di Clinica e Studio Medico'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Zeigen Sie Ihre Einrichtung, Ihr Team und Ihre Expertise mit professionellen Präsentationsvideos.' :
                     currentLanguage === 'en' ? 'Showcase your facility, team, and expertise with professional presentation videos.' :
                     currentLanguage === 'fr' ? 'Présentez votre établissement, votre équipe et votre expertise avec des vidéos de présentation professionnelles.' :
                     'Mostra la tua struttura, il tuo team e la tua esperienza con video di presentazione professionali.'}
                  </p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Gesundheitskampagnen' :
                     currentLanguage === 'en' ? 'Health Campaigns' :
                     currentLanguage === 'fr' ? 'Campagnes de Santé' :
                     'Campagne Sanitarie'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Erstellen Sie Aufklärungskampagnen und fördern Sie Gesundheitsinitiativen mit überzeugenden Videoinhalten.' :
                     currentLanguage === 'en' ? 'Create awareness campaigns and promote health initiatives with compelling video content.' :
                     currentLanguage === 'fr' ? 'Créez des campagnes de sensibilisation et promouvez des initiatives de santé avec un contenu vidéo convaincant.' :
                     'Crea campagne di sensibilizzazione e promuovi iniziative sanitarie con contenuti video convincenti.'}
                  </p>
                </div>
              </div>
            </div>
          </section>
          
          <ExampleGallery industry="healthcare" />
          
          <FAQSection industry="healthcare" />
          
          <CtaSection industry="healthcare" />
        </main>
      </PageTransition>
      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default HealthcareIndustry;
